<template>
    <div 
        class="kt-context-menu-wrapper" 
        ref="wrapperRef"
        @contextmenu.prevent="handleContextMenu"
    >
        <!-- The target element slot -->
        <slot name="target">
            <div v-if="props.targetText" class="kt-context-menu-target">{{ props.targetText }}</div>
        </slot>
        
        <!-- Context Menu -->
        <Transition name="context-menu-fade">
            <div
                v-if="isVisible"
                ref="menuRef"
                :class="['kt-context-menu', props.menuClass]"
                :style="menuStyle"
                v-click-outside="hideMenu"
                @click.stop
            >
                <slot name="menu">
                    <ul class="dropdown-menu show">
                        <li 
                            v-for="(item, index) in props.items" 
                            :key="index"
                            :class="[
                                'dropdown-item',
                                { 'disabled': item.disabled, 'active': item.active }
                            ]"
                            @click="handleItemClick(item)"
                        >
                            <slot name="item" :item="item">
                                <span v-if="item.icon" class="me-2">
                                    <i :class="item.icon"></i>
                                </span>
                                <span v-if="item.label">{{ item.label }}</span>
                                <span v-if="item.shortcut" class="ms-auto text-muted small">{{ item.shortcut }}</span>
                            </slot>
                        </li>
                        <li v-if="props.items.length === 0" class="dropdown-item disabled">
                            <slot name="empty">
                                <span class="text-muted">No options available</span>
                            </slot>
                        </li>
                    </ul>
                </slot>
            </div>
        </Transition>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue";

/**
 * @name KtContextMenu
 * @description A customizable context menu component triggered by right-clicking on a target element.
 * @slot target - The element that triggers the context menu on right-click
 * @slot menu - The entire menu content (replaces default menu)
 * @slot item - Individual menu item template
 * @slot empty - Content shown when no items are provided
 */

export interface ContextMenuItem {
    /** Unique identifier for the item */
    id?: string | number;
    /** Display label */
    label?: string;
    /** FontAwesome icon class (e.g., 'fas fa-edit') */
    icon?: string;
    /** Whether item is disabled */
    disabled?: boolean;
    /** Whether item is active/selected */
    active?: boolean;
    /** Keyboard shortcut display (e.g., 'Ctrl+C') */
    shortcut?: string;
    /** Any additional data */
    data?: any;
}

interface Props {
    /** Text for the target element (alternative to target slot) */
    targetText?: string;
    /** Menu items to display */
    items?: ContextMenuItem[];
    /** Custom CSS class for the menu */
    menuClass?: string;
    /** Minimum width of the menu (px or css value) */
    minWidth?: string;
    /** Maximum height of the menu (px or css value) */
    maxHeight?: string;
    /** Whether the menu is disabled */
    disabled?: boolean;
    /** Whether to close menu when clicking outside */
    closeOnOutsideClick?: boolean;
    /** Whether to close menu when clicking an item */
    closeOnItemClick?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    targetText: '',
    items: () => [],
    menuClass: '',
    minWidth: '150px',
    maxHeight: '300px',
    disabled: false,
    closeOnOutsideClick: true,
    closeOnItemClick: true
});

const emit = defineEmits<{
    (e: 'show', position: { x: number; y: number }): void;
    (e: 'hide'): void;
    (e: 'select', item: ContextMenuItem): void;
}>();

/**
 * Custom directive to detect clicks outside an element.
 */
const vClickOutside = {
    beforeMount: (el: any, binding: any) => {
        el.clickOutsideEvent = (event: MouseEvent) => {
            if (!(el === event.target || el.contains(event.target))) {
                binding.value();
            }
        };
        document.addEventListener("click", el.clickOutsideEvent);
    },
    unmounted: (el: any) => {
        document.removeEventListener("click", el.clickOutsideEvent);
    },
};

const wrapperRef = ref<HTMLElement | null>(null);
const menuRef = ref<HTMLElement | null>(null);
const isVisible = ref(false);
const menuPosition = ref({ x: 0, y: 0 });

const menuStyle = computed(() => ({
    left: `${menuPosition.value.x}px`,
    top: `${menuPosition.value.y}px`,
    minWidth: props.minWidth,
    maxHeight: props.maxHeight
}));

/**
 * Show context menu at click position
 */
const handleContextMenu = (event: MouseEvent): void => {
    if (props.disabled) return;

    event.preventDefault();
    event.stopPropagation();

    // Calculate initial position
    let x = event.clientX;
    let y = event.clientY;

    menuPosition.value = { x, y };
    isVisible.value = true;

    emit('show', { x, y });

    // Adjust position after menu renders to keep it in viewport
    nextTick(() => {
        adjustPosition();
    });
};

/**
 * Adjust menu position to ensure it stays within viewport
 */
const adjustPosition = (): void => {
    if (!menuRef.value) return;

    const menuRect = menuRef.value.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let x = menuPosition.value.x;
    let y = menuPosition.value.y;

    // Adjust if menu goes off right edge
    if (x + menuRect.width > viewportWidth) {
        x = viewportWidth - menuRect.width - 8;
    }

    // Adjust if menu goes off bottom edge
    if (y + menuRect.height > viewportHeight) {
        y = viewportHeight - menuRect.height - 8;
    }

    // Ensure minimum 8px from edges
    x = Math.max(8, x);
    y = Math.max(8, y);

    menuPosition.value = { x, y };
};

/**
 * Hide the context menu
 */
const hideMenu = (): void => {
    if (!isVisible.value) return;
    isVisible.value = false;
    emit('hide');
};

/**
 * Handle menu item click
 */
const handleItemClick = (item: ContextMenuItem): void => {
    if (item.disabled) return;

    emit('select', item);

    if (props.closeOnItemClick) {
        hideMenu();
    }
};

// Close on Escape key
const handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && isVisible.value) {
        hideMenu();
    }
};

onMounted(() => {
    document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown);
});

// Expose methods for programmatic control
defineExpose({
    show: (x: number, y: number) => {
        menuPosition.value = { x, y };
        isVisible.value = true;
        nextTick(adjustPosition);
    },
    hide: hideMenu,
    isVisible: () => isVisible.value
});
</script>

<style scoped>
.kt-context-menu-wrapper {
    display: inline-block;
}

.kt-context-menu-target {
    cursor: context-menu;
}

.kt-context-menu {
    position: fixed;
    z-index: 1060;
}

.kt-context-menu .dropdown-menu {
    display: block;
    position: static;
    min-width: v-bind('props.minWidth');
    max-height: v-bind('props.maxHeight');
    overflow-y: auto;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.kt-context-menu .dropdown-item {
    display: flex;
    align-items: center;
    padding: 0.5rem 1rem;
    cursor: pointer;
}

.kt-context-menu .dropdown-item:hover:not(.disabled) {
    background-color: #f8f9fa;
}

.kt-context-menu .dropdown-item.active {
    background-color: #0d6efd;
    color: #fff;
}

.kt-context-menu .dropdown-item.active .text-muted {
    color: rgba(255, 255, 255, 0.7) !important;
}

.kt-context-menu .dropdown-item.disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Transitions */
.context-menu-fade-enter-active,
.context-menu-fade-leave-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
}

.context-menu-fade-enter-from,
.context-menu-fade-leave-to {
    opacity: 0;
    transform: scale(0.95);
}
</style>
